import type { CSSProperties, ForwardedRef, MouseEventHandler } from 'react';
import { forwardRef, useId } from 'react';

import classNames from 'classnames';

import styles from './ToggleCard.module.css';

import { Button, type LegacyButtonProps } from '../Button';
import type { CheckboxProps } from '../Checkbox';
import { Checkbox } from '../Checkbox';
import type { PictureProps } from '../Picture';
import { Picture } from '../Picture';
import { Text } from '../Text';

export interface ToggleCardProps extends Pick<
  CheckboxProps,
  'name' | 'value' | 'checked' | 'onChange' | 'onBlur'
> {
  /** The heading of the card. */
  heading: string;
  /** The description of the card. */
  description?: string;

  /** The heading for additional messaging */
  additionalMessagingHeading?: string;
  /** The description for additional messaging */
  additionalMessagingDescription?: string;

  /** The sources of the {@link Picture} to display at the top of the card. */
  imageSources: PictureProps['sources'];
  /** The alt-text for the image. */
  imageAlt: string;

  /** The label for the checkbox while it is not checked. */
  label: string;
  /** The label for the checkbox while it is checked. */
  checkedLabel?: string;

  /** Additional props to pass to the checkbox. */
  checkboxProps?: Omit<
    CheckboxProps,
    'name' | 'value' | 'checked' | 'onChange' | 'onBlur' | 'ref'
  >;

  /** Add a badge to the card. */
  badge?: string;

  /** The label of the button. */
  buttonLabel?: string;
  /** The callback for the button click. */
  onButtonClick?: MouseEventHandler<HTMLButtonElement>;

  /** Additional props to pass to the button. */
  buttonProps?: Omit<LegacyButtonProps, 'children' | 'onClick'>;

  className?: string;

  /** Optional class to modify the internal content container */
  contentClassName?: string;
  /** Optional class to modify the internal control container */
  controlClassName?: string;
  style?: CSSProperties;
}

/**
 * Render a toggle switch styled as a card. Works like a {@link Checkbox} with
 * extra content, such as a badge, an image, and a button.
 *
 * Use the {@link Grid} component to stack multiple cards horizontally.
 *
 * @param props - The {@link ToggleCardProps} to render the input field.
 * @see https://corgi-x.tfd.engineering/components/togglecard
 */
export const ToggleCard = forwardRef(ToggleCard_);

export function ToggleCard_(
  props: ToggleCardProps,
  ref?: ForwardedRef<HTMLInputElement>
) {
  const { label, checkedLabel = props.label } = props;
  const headingId = useId();
  const checkboxId = useId();
  const descriptionId = useId();

  return (
    // We use a label here instead of a div so that the checkbox is clickable.
    <div
      role="group"
      className={classNames(styles.group, props.className)}
      style={props.style}
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
    >
      <div className={styles.container}>
        <div className={styles.imageContainer}>
          <Picture
            sources={props.imageSources}
            alt={props.imageAlt}
            className={styles.image}
          />
        </div>
        {props.badge && (
          <div className={styles.badgeContainer}>
            <Text role="status" variant="heading-12" className={styles.badge}>
              {props.badge}
            </Text>
          </div>
        )}
        <div className={classNames(styles.content, props.contentClassName)}>
          <div className={styles.contentTexts}>
            <Text
              id={headingId}
              variant="heading-22"
              bold
              color="Charcoal3"
              as="h5"
            >
              {props.heading}
            </Text>
            {props.additionalMessagingHeading &&
              props.additionalMessagingDescription && (
                <div className={styles.additionalMessaging}>
                  <Text
                    weight="semibold"
                    color="Charcoal3"
                    variant="heading-12"
                  >
                    {props.additionalMessagingHeading}
                  </Text>
                  <Text color="Charcoal3" variant="heading-12" as="p">
                    {props.additionalMessagingDescription}
                  </Text>
                </div>
              )}
            {props.description && (
              <Text id={descriptionId} variant="heading-12" as="p">
                {props.description}
              </Text>
            )}
          </div>
          {props.buttonLabel && (
            <Button
              type="button"
              variant="plain-text"
              onClick={props.onButtonClick}
              className={styles.button}
              aria-label={props.buttonLabel}
              {...props.buttonProps}
            >
              {props.buttonLabel}
            </Button>
          )}
        </div>
        <div className={classNames(styles.control, props.controlClassName)}>
          <Checkbox
            ref={ref}
            id={checkboxId}
            name={props.name}
            value={props.value}
            checked={props.checked}
            label={props.checked ? checkedLabel || label : label}
            aria-label={props.checked ? checkedLabel || label : label}
            aria-live="polite"
            onBlur={props.onBlur}
            onChange={props.onChange}
            {...props.checkboxProps}
          />
        </div>
      </div>
    </div>
  );
}
